import {
  ArrowsPointingInIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { XCloseIcon } from '@langchain/untitled-ui-icons';

import { TrashIcon } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { HubCard } from '@/Pages/HubRepo/HubCard';
import { usePlaygroundStore } from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { EditableChatInput } from '@/components/EditableChatInput';
import { EditableMessage } from '@/components/EditableMessage';
import { EditInputFunctionParameters } from '@/components/ExampleCrudPane/EditInputFunctions';
import PromptTemplateFormatDropdown from '@/components/PromptTemplateFormatDropdown';
import { SchemaEditorModal } from '@/components/SchemaEditor/SchemaEditorModal';
import { SchemaEditorVariant } from '@/components/SchemaEditor/types';
import { Button, ButtonGroup, IconButton, Tooltip } from '@/design-system';
import ChainIcon from '@/icons/ChainIcon.svg?react';
import { MessageCapability, S3DataSchema, StoredMessage } from '@/types/schema';
import { isStoredMessage } from '@/utils/messages';

import {
  KwargsSerializedConstructor,
  ManifestEditorProps,
  ManifestHubProps,
} from '../../Manifest.utils';
import {
  SerializedChatPromptTemplateMessagesKwarg,
  convertManifestToMessages,
  convertMessagesToManifestKwargs,
  extractTemplateFormatFromKwargs,
} from './ManifestChatPromptTemplate.utils';

const CAPABILITIES = [MessageCapability.IMAGE, MessageCapability.CANVAS];

export const SchemaNoSpacesTooltip = () => (
  <Tooltip title="Schema names cannot have spaces. Please use underscores or camel case.">
    <div>
      <InformationCircleIcon className="my-auto h-4 w-4 text-ls-white" />
    </div>
  </Tooltip>
);

export type StructuredPromptSchema = {
  type: string;
  properties: Record<string, unknown>;
  required: string[];
  description: string;
  title: string;
  strict?: boolean;
};

type SerializedStructuredPrompt = KwargsSerializedConstructor<{
  schema_: StructuredPromptSchema;
  input_variables: string[];
  messages: SerializedChatPromptTemplateMessagesKwarg | StoredMessage[];
}>;

export function ManifestStructuredPrompt(
  props: ManifestEditorProps<SerializedStructuredPrompt>
) {
  const { value, onChange } = props;
  const kwargs = value.kwargs;
  const { examples } = usePlaygroundStore();

  const exampleAttachmentUrls = useMemo(() => {
    return examples.reduce((acc, example) => {
      return { ...acc, ...(example.attachment_urls ?? {}) };
    }, {} as Record<string, S3DataSchema>);
  }, [examples]);

  // check if messages is a StoredMessage - if it is, it's already in message format
  // so we don't need to convert it
  const messageAlreadyFormatted = kwargs.messages?.every((message) =>
    isStoredMessage(message)
  );

  const messages = messageAlreadyFormatted
    ? ((kwargs.messages ?? []) as StoredMessage[])
    : convertManifestToMessages(kwargs.messages ?? []);

  const [isMinimized, setIsMinimized] = useState(props.minimizedSchema);

  const valueRef = useRef(value);
  valueRef.current = value;

  const setMessages = useCallback(
    (changeMsgs, newTemplateFormat) => {
      const templateFormat = newTemplateFormat ?? 'f-string';
      onChange({
        ...valueRef.current,
        kwargs: {
          ...valueRef.current?.kwargs,
          ...convertMessagesToManifestKwargs(changeMsgs, templateFormat),
        },
      });
    },
    [onChange]
  );

  const capabilities = useMemo(() => {
    return CAPABILITIES.filter(
      (c) => !props.disabledMessageCapabilities?.includes(c)
    );
  }, [props.disabledMessageCapabilities]);

  return (
    <>
      <EditableChatInput
        variant="template"
        capabilities={capabilities}
        readOnly={props.readOnly}
        templateFormat={extractTemplateFormatFromKwargs(kwargs)}
        messages={messages}
        setMessages={setMessages}
        isAllCollapsed={props.isAllCollapsed}
        setIsAllCollapsed={props.setIsAllCollapsed}
        hideTemplateFormatSelector={props.hideTemplateFormatSelector}
        attachments={exampleAttachmentUrls}
        actions={
          <>
            {(isMinimized || props.outputSchemaAsModal) &&
            props.onDeleteSchema ? (
              <OutputSchemaButton
                onClick={() => setIsMinimized(false)}
                onDelete={props.onDeleteSchema}
              />
            ) : null}
            {props.actions}
          </>
        }
        variableMappingProps={props.variableMappingProps}
        onVariableCreated={props.onVariableCreated}
        existingVariables={value.kwargs.input_variables ?? []}
      />

      {!isMinimized ? (
        props.outputSchemaAsModal ? (
          <SchemaEditorModal
            isOpen={!isMinimized}
            onClose={() => setIsMinimized(true)}
            value={kwargs.schema_}
            onSubmit={(schema) => {
              props.onChange({
                ...props.value,
                kwargs: {
                  ...kwargs,
                  schema_: schema as StructuredPromptSchema,
                },
              });
              setIsMinimized(true);
            }}
            onRemove={() => {
              props.onDeleteSchema?.();
              setIsMinimized(true);
            }}
          />
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            <h2 className="flex flex-row gap-2 text-sm font-semibold leading-[27px]">
              Output schema
              <SchemaNoSpacesTooltip />
              {props.onDeleteSchema && (
                <Tooltip title="Remove output schema" side="top">
                  <button
                    type="button"
                    onClick={props.onDeleteSchema}
                    className="ml-auto flex h-[26px] w-[26px] items-center justify-center rounded-md border border-secondary p-1 transition-all hover:bg-secondary-hover active:bg-secondary"
                    aria-label="Remove output schema"
                  >
                    <TrashIcon />
                  </button>
                </Tooltip>
              )}
              {props.onDeleteSchema && (
                <Tooltip title="Minimize" side="top">
                  <button
                    type="button"
                    onClick={() => setIsMinimized(true)}
                    className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-secondary p-1 transition-all hover:bg-secondary-hover active:bg-secondary"
                  >
                    <ArrowsPointingInIcon className="h-4 w-4" />
                  </button>
                </Tooltip>
              )}
            </h2>

            <EditInputFunctionParameters
              variant={SchemaEditorVariant.SCHEMA}
              value={kwargs.schema_}
              onChange={(value) =>
                props.onChange({
                  ...props.value,
                  kwargs: {
                    ...kwargs,
                    schema_: value as StructuredPromptSchema,
                  },
                })
              }
              showEditorSelector
              showBorder={true}
            />
          </div>
        )
      ) : null}
    </>
  );
}

export function ManifestStructuredPromptHub(
  props: ManifestHubProps<SerializedStructuredPrompt>
) {
  const kwargs = props.value.kwargs;
  const messages = convertManifestToMessages(kwargs.messages ?? []);

  return (
    <HubCard
      {...props.cardProps}
      title="StructuredPrompt"
      icon={<ChainIcon className="h-4 w-4" />}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {messages.map((message, idx) => {
            return (
              <div key={idx} className="cursor-pointer rounded-lg">
                <EditableMessage
                  variant="template"
                  messageValue={message}
                  onChange={() => null}
                  hideSyntaxHighlighting={false}
                  templateFormat={
                    extractTemplateFormatFromKwargs(kwargs) ?? 'f-string'
                  }
                  readOnly
                />
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-4">
          <EditInputFunctionParameters
            variant={SchemaEditorVariant.SCHEMA}
            value={kwargs.schema_}
            onChange={() => void 0}
            readOnly
            showBorder={true}
          />
        </div>
      </div>

      <div className="ml-auto mt-4 w-fit">
        <PromptTemplateFormatDropdown
          templateFormat={extractTemplateFormatFromKwargs(kwargs) ?? 'f-string'}
          disabled
        />
      </div>
    </HubCard>
  );
}

const OutputSchemaButton = ({
  onClick,
  onDelete,
}: {
  onClick: () => void;
  onDelete: () => void;
}) => {
  return (
    <ButtonGroup variant="outlined">
      <Button onClick={onClick}>Output schema</Button>
      <IconButton
        icon={XCloseIcon}
        label="Remove output schema"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      />
    </ButtonGroup>
  );
};
